import { ChangeDetectionStrategy, Component, effect, inject, Injector, OnDestroy } from '@angular/core';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SseService } from '@ortho-next/sse';
import { ToastService } from '../../core';
import { BaseHelpService, HelpService, ModelService } from '../../services';
import { AnatomicalSideLabelPipe, BaseNavbarComponent, BoneTypeLabelPipe, ClosureModalComponent, ConfirmationComponent, HelpComponent, PlanTypeLabelPipe, ReplaceLongLegPipe } from '../../shared';


/**
 * Horizontal bar on top.
 */
@Component({
	selector: 'navbar',
	templateUrl: '../../shared/base-navbar/base-navbar.component.html',
	imports: [
		NgbDropdownModule,
		BoneTypeLabelPipe,
		PlanTypeLabelPipe,
		ReplaceLongLegPipe,
		AnatomicalSideLabelPipe
	],
	changeDetection: ChangeDetectionStrategy.Eager,
})
export class NavbarComponent extends BaseNavbarComponent implements OnDestroy {

	private readonly sse = inject(SseService);
	private readonly modalSrv = inject(NgbModal);
	private readonly injector = inject(Injector);
	private readonly modelSrv = inject(ModelService);
	private readonly toastSrv = inject(ToastService);

	override readonly isSaveVisible = true;

	private resetKey?: string;

	private readonly init = effect(() => {
		if (this.plan()) this.handlePlanResetting();
	});

	/**
	 * Persist model scene.
	 */
	override save(): void {
		if (!this.isReadOnly()) {
			const currentCase = this.canvasSrv.currentCase!;
			this.modelSrv.persistModel(currentCase.id, currentCase.userGuid).subscribe({
				next: () => this.toastSrv.showSuccess(this.labels().NAVBAR_COMPONENT_PLAN_SAVED_SUCCESS),
				error: () => this.toastSrv.showError(this.labels().NAVBAR_COMPONENT_PLAN_SAVED_ERROR)
			});
		}
	}

	/**
	* Open modal of help info.
	*/
	override openHelpModal(): void {
		if (!this.modalSrv.hasOpenModals()) {
			this.modalSrv.open(HelpComponent, {
				backdropClass: 'wrapper-modal-help', windowClass: 'wrapper-modal-help', injector:
					Injector.create({
						parent: this.injector,
						providers: [{ provide: BaseHelpService, useClass: HelpService }]
					})
			});
		}
	}

	/**
	* Open modal of confirmation before exit.
	*/
	override openClosingModal(): void {
		if (!this.modalSrv.hasOpenModals()) {
			this.modalSrv.open(ClosureModalComponent, { centered: true, backdrop: 'static' });
		}
	}

	/**
	 * Subscribe to analysis resetting event.
	 */
	private handlePlanResetting(): void {
		if (!this.resetKey) {
			const caseGuid = this.plan()?.id;
			this.resetKey = `OrthoNext.Fibone.Reset.${caseGuid}`;
			setTimeout(() => {
				this.sse.subscribe(this.resetKey!, (success: boolean) => {
					if (success) {
						this.modalSrv.dismissAll();
						this.openPlanChangedWarning(caseGuid!);
					}
				});
			}, 5000);
		}
	}

	private openPlanChangedWarning(caseGuid: string): void {
		const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().INVALID_PLAN_MODAL_TITLE,
			message: this.labels().PLAN_CHANGED_WARNING_MESSAGE,
			confirmBtn: this.labels().INVALID_PLAN_MODAL_CONFIRM_BTN,
			isCancelBtn: false
		};
		modalRef.result.then(() => globalThis.location.href = `${this.config.planDetailsSite}${caseGuid}`).catch(err => false);
	}


	ngOnDestroy(): void {
		this.resetKey && this.sse.unsubscribe(this.resetKey);
	}

}
